import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TextField,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CachedIcon from '@mui/icons-material/Cached';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { getDateRange } from '@/utils/date-range.ts';

import { toastService } from '@/services/toast.service';
import { DashboardLayout } from '@/components/DashboardLayout';
import { PageHint } from '@components/common/PageHint';
import { PageCard } from '@components/common/PageCard';
import { BaseDialog } from '@components/common/BaseDialog';
import { UniversalPagination } from '@components/common/UniversalPagination';
import { DateRangeNavigator, DateRangeView } from '@components/common/DateRangeNavigator';
import {
  getSelfActivityReport,
  downloadSelfActivityReportPdf,
  getSelfAiContext,
  getSelfAiAnalysis,
  getReportOptions,
  getMyAiReports,
  getMyAiReport,
  ReportSection,
} from '@services/self-reports.service';
import {
  SelfActivityReport,
  ReportOptionsEventType,
  ReportOptionsSection,
  AiReportListItem,
  AiReportDetail,
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
  AI_REPORT_TYPE_LABELS,
} from '@/types/client-reports';
import { EventTypeIcon } from '@components/clients/calendar/EventTypeIcon';
import { htmlToMarkdown } from '@/utils/html-to-markdown';
import { AdaptiveTabs, AdaptiveTabItem } from '@components/common/AdaptiveTabs';

const DATE_VIEW_KEY = 'my-reports-date-view';
const DATE_KEY = 'my-reports-current-date';
const SECTIONS_KEY = 'my-reports-sections';
const AI_HISTORY_LIMIT_KEY = 'my-reports-ai-history-limit';

const AI_REPORT_STATUS_LABELS: Record<string, string> = {
  pending: 'Очікує',
  completed: 'Готово',
  failed: 'Помилка',
};

const AI_REPORT_STATUS_COLORS: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  completed: 'success',
  failed: 'error',
};

const SECTION_LABELS: Partial<Record<ReportSection, string>> = {
  profile: 'Профіль',
  measurements: 'Виміри',
  metrics: 'Метрики',
  goals: 'Цілі',
  appointments: 'Зустрічі',
  events: 'Події',
  files: 'Файли',
  questionnaires: 'Опитувальники',
};

const SECTION_ICONS: Partial<Record<ReportSection, React.ReactElement>> = {
  profile: <PersonIcon fontSize="inherit" />,
  measurements: <MonitorWeightIcon fontSize="inherit" />,
  metrics: <TrendingUpIcon fontSize="inherit" />,
  goals: <FlagIcon fontSize="inherit" />,
  appointments: <EventAvailableIcon fontSize="inherit" />,
  events: <EventNoteIcon fontSize="inherit" />,
  files: <InsertDriveFileIcon fontSize="inherit" />,
  questionnaires: <QuestionAnswerIcon fontSize="inherit" />,
};


const ACTIVITY_LEVEL_LABELS: Record<string, string> = {
  sedentary: 'Сидячий спосіб життя',
  lightly_active: 'Легка активність',
  moderately_active: 'Помірна активність',
  very_active: 'Висока активність',
  extra_active: 'Дуже висока активність',
};

const GOAL_LABELS: Record<string, string> = {
  wellbeing_improvement: 'Покращення самопочуття',
  weight_loss: 'Схуднення',
  weight_gain: 'Набір ваги',
  muscle_gain: 'Набір м\'язів',
  maintenance: 'Підтримка форми',
};

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Box>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MY_REPORTS_TABS: AdaptiveTabItem[] = [
  { value: 'activity', label: 'Звіт', icon: <AssessmentIcon sx={{ fontSize: 16 }} />, iDoc: TabIndices.REPORTS_ACTIVITY },
  { value: 'history', label: 'Архів AI-аналізів', icon: <HistoryIcon sx={{ fontSize: 16 }} />, iDoc: TabIndices.REPORTS_AI_HISTORY },
];

export const MyReportsPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const stored = localStorage.getItem(DATE_KEY);
    return stored ? new Date(stored) : new Date();
  });

  const [dateView, setDateView] = useState<DateRangeView>(
    (localStorage.getItem(DATE_VIEW_KEY) as DateRangeView) || 'month',
  );

  const [sections, setSections] = useState<ReportSection[]>(() => {
    const stored = localStorage.getItem(SECTIONS_KEY);
    if (stored) {
      try { return JSON.parse(stored) as ReportSection[]; }
      catch { return []; }
    }
    return [];
  });

  const [availableSections, setAvailableSections] = useState<ReportOptionsSection[]>([]);
  const [eventTypes, setEventTypes] = useState<ReportOptionsEventType[]>([]);
  // null = всі типи, інакше — вибрані IDs
  const [selectedEventTypeIds, setSelectedEventTypeIds] = useState<number[] | null>(null);

  const [report, setReport] = useState<SelfActivityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [aiContextOpen, setAiContextOpen] = useState(false);
  const [aiContextMarkdown, setAiContextMarkdown] = useState<string>('');
  const [loadingAiContext, setLoadingAiContext] = useState(false);
  const [aiContextCopied, setAiContextCopied] = useState(false);

  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(false);
  const [aiAnalysisPrompt, setAiAnalysisPrompt] = useState('');
  const [aiAnalysisText, setAiAnalysisText] = useState<string>('');
  const [aiAnalysisFromCache, setAiAnalysisFromCache] = useState(false);
  const [loadingAiAnalysis, setLoadingAiAnalysis] = useState(false);
  const [aiAnalysisCopied, setAiAnalysisCopied] = useState(false);

  // Tabs — запам'ятовується через URL (?tab=activity|history)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'history' ? 'history' : 'activity';
  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });
  };

  // AI-архів
  const [aiHistory, setAiHistory] = useState<AiReportListItem[]>([]);
  const [aiHistoryTotal, setAiHistoryTotal] = useState(0);
  const [aiHistoryPage, setAiHistoryPage] = useState(1);
  const [aiHistoryLimit, setAiHistoryLimit] = useState(10);
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);
  const [aiHistoryError, setAiHistoryError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<AiReportDetail | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedReportCopied, setSelectedReportCopied] = useState(false);

  // Завантажуємо довідники один раз
  useEffect(() => {
    getReportOptions()
      .then((opts) => {
        setEventTypes(opts.eventTypes);
        setAvailableSections(opts.sections);
        setSections((prev) => {
          if (prev.length > 0) return prev;
          return opts.sections.map((s) => s.key as ReportSection);
        });
      })
      .catch(() => {/* нема options — фільтри не показуємо */});
  }, []);

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
    localStorage.setItem(DATE_KEY, date.toISOString());
  };

  const handleViewChange = (view: DateRangeView) => {
    setDateView(view);
    localStorage.setItem(DATE_VIEW_KEY, view);
  };

  const handleSectionsChange = (_: React.MouseEvent<HTMLElement>, value: ReportSection[]) => {
    if (value.length === 0) return;
    setSections(value);
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(value));
  };

  const handleEventTypeToggle = (_: React.MouseEvent<HTMLElement>, value: number[]) => {
    if (value.length === 0) return;
    setSelectedEventTypeIds(value.length === eventTypes.length ? null : value);
  };

  const activeSections = sections.length > 0 && sections.length < availableSections.length ? sections : undefined;
  const activeEventTypeIds = selectedEventTypeIds ?? undefined;

  useEffect(() => {
    const { from, to } = getDateRange(currentDate, dateView);
    setLoading(true);
    setError(null);
    getSelfActivityReport(from, to, activeSections, activeEventTypeIds)
      .then(setReport)
      .catch(() => setError('Не вдалося завантажити звіт'))
      .finally(() => setLoading(false));
  }, [currentDate, dateView, sections, selectedEventTypeIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadPdf = async () => {
    const { from, to } = getDateRange(currentDate, dateView);
    setDownloadingPdf(true);
    try {
      await downloadSelfActivityReportPdf(from, to, activeSections, activeEventTypeIds);
    } catch {
      toastService.error('Не вдалося завантажити PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleOpenAiContext = async () => {
    const { from, to } = getDateRange(currentDate, dateView);
    setLoadingAiContext(true);
    setAiContextMarkdown('');
    setAiContextOpen(true);
    try {
      const markdown = await getSelfAiContext(from, to, activeSections);
      setAiContextMarkdown(markdown);
    } catch {
      toastService.error('Не вдалося завантажити AI-контекст');
      setAiContextOpen(false);
    } finally {
      setLoadingAiContext(false);
    }
  };

  const handleOpenAiAnalysis = () => {
    setAiAnalysisText('');
    setAiAnalysisOpen(true);
  };

  const handleRunAiAnalysis = async () => {
    const { from, to } = getDateRange(currentDate, dateView);
    setLoadingAiAnalysis(true);
    setAiAnalysisText('');
    setAiAnalysisFromCache(false);
    setAiAnalysisCopied(false);
    try {
      const result = await getSelfAiAnalysis(from, to, activeSections, aiAnalysisPrompt.trim() || undefined);
      setAiAnalysisText(result.html);
      setAiAnalysisFromCache(result.fromCache);
    } catch (e: any) {
      toastService.error(e?.message ?? 'Не вдалося отримати AI-аналіз');
    } finally {
      setLoadingAiAnalysis(false);
    }
  };

  const handleDownloadAiAnalysis = () => {
    const { from, to } = getDateRange(currentDate, dateView);
    const html = `<!DOCTYPE html>
<html lang="uk">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>AI-аналіз ${from} – ${to}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 24px; color: #1a1a1a; line-height: 1.7; }
    h2, h3 { margin-top: 1.5em; margin-bottom: 0.4em; }
    p { margin: 0 0 0.8em; }
    ul, ol { padding-left: 1.5em; margin-bottom: 0.8em; }
    li { margin-bottom: 0.2em; }
    strong { font-weight: 600; }
  </style>
</head>
<body>${aiAnalysisText}</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analysis_${from}_${to}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyAiAnalysis = () => {
    navigator.clipboard.writeText(htmlToMarkdown(aiAnalysisText)).then(() => {
      setAiAnalysisCopied(true);
      setTimeout(() => setAiAnalysisCopied(false), 2000);
    });
  };

  const handleCloseAiAnalysis = () => {
    if (loadingAiAnalysis) return;
    setAiAnalysisOpen(false);
    setAiAnalysisPrompt('');
    setAiAnalysisText('');
    setAiAnalysisFromCache(false);
    setAiAnalysisCopied(false);
  };

  // Завантаження архіву AI-аналізів
  useEffect(() => {
    if (activeTab !== 'history') return;
    setAiHistoryLoading(true);
    setAiHistoryError(null);
    const offset = (aiHistoryPage - 1) * aiHistoryLimit;
    getMyAiReports(aiHistoryLimit, offset)
      .then(({ data, meta }) => {
        setAiHistory(data);
        setAiHistoryTotal(meta.total);
      })
      .catch(() => setAiHistoryError('Не вдалося завантажити архів'))
      .finally(() => setAiHistoryLoading(false));
  }, [activeTab, aiHistoryPage, aiHistoryLimit]);

  const handleViewReport = async (id: number) => {
    setLoadingReport(true);
    setSelectedReportCopied(false);
    try {
      const detail = await getMyAiReport(id);
      setSelectedReport(detail);
    } catch {
      toastService.error('Не вдалося завантажити звіт');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleCloseReport = () => { setSelectedReport(null); setSelectedReportCopied(false); };

  const handleCopySelectedReport = () => {
    if (!selectedReport?.response) return;
    navigator.clipboard.writeText(htmlToMarkdown(selectedReport.response)).then(() => {
      setSelectedReportCopied(true);
      setTimeout(() => setSelectedReportCopied(false), 2000);
    });
  };

  const handleDownloadReport = (report: AiReportDetail) => {
    const html = `<!DOCTYPE html>
<html lang="uk">
<head>
  <meta charset="UTF-8" />
  <title>AI-аналіз ${report.periodFrom} – ${report.periodTo}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 24px; color: #1a1a1a; line-height: 1.7; }
    h2, h3 { margin-top: 1.5em; margin-bottom: 0.4em; }
    p { margin: 0 0 0.8em; }
    ul, ol { padding-left: 1.5em; margin-bottom: 0.8em; }
    li { margin-bottom: 0.2em; }
    strong { font-weight: 600; }
  </style>
</head>
<body>${report.response ?? ''}</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analysis_${report.periodFrom}_${report.periodTo}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };


  return (
    <DashboardLayout data-i-doc={PageIndices.CLIENT_MY_REPORTS}>
      <Box>
        <PageHint iDoc={PageIndices.CLIENT_MY_REPORTS} />

        <Box sx={{ mb: 2 }}>
          <AdaptiveTabs tabs={MY_REPORTS_TABS} value={activeTab} onChange={handleTabChange} />
        </Box>

        {activeTab === 'activity' && (
        <PageCard>
          {/* Toolbar */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
            <Box display="flex" flexDirection="column" gap={1}>
              <DateRangeNavigator
                currentDate={currentDate}
                onDateChange={handleDateChange}
                view={dateView}
                onViewChange={handleViewChange}
              />

              {/* Секції — рендеримо тільки якщо options завантажились */}
              {availableSections.length > 0 && (
                <Tooltip title="Оберіть секції для включення у звіт" placement="bottom-start">
                  <ToggleButtonGroup
                    value={sections}
                    onChange={handleSectionsChange}
                    size="small"
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {availableSections.map((s) => (
                      <ToggleButton key={s.key} value={s.key} sx={{ gap: 0.5, fontSize: '0.75rem', py: 0.5 }}>
                        {SECTION_ICONS[s.key as ReportSection]}
                        {SECTION_LABELS[s.key as ReportSection] ?? s.key}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Tooltip>
              )}

              {/* Фільтр по типах подій — показуємо тільки якщо секція events активна і є типи */}
              {sections.includes('events') && eventTypes.length > 0 && (
                <ToggleButtonGroup
                  value={selectedEventTypeIds ?? eventTypes.map((t) => t.id)}
                  onChange={handleEventTypeToggle}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {eventTypes.map((et) => (
                    <ToggleButton key={et.id} value={et.id} sx={{ gap: 0.5, fontSize: '0.75rem', py: 0.5 }}>
                      <EventTypeIcon code={et.code} size={14} />
                      {et.name}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </Box>

            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleOpenAiAnalysis}
                disabled={loadingAiAnalysis || loading}
              >
                AI-аналіз
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || loading || !report}
              >
                {downloadingPdf ? 'Генерація...' : 'Завантажити PDF'}
              </Button>
            </Box>
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && report && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {sections.includes('profile') && report.profile && (
                  <Chip icon={<PersonIcon />} label="Профіль" variant="outlined" />
                )}
                {sections.includes('metrics') && report.metrics && (
                  <Chip icon={<TrendingUpIcon />} label="Метрики" variant="outlined" color="info" />
                )}
                {sections.includes('goals') && report.goals && (
                  <Chip icon={<FlagIcon />} label="Цілі" variant="outlined" color="warning" />
                )}
                {sections.includes('appointments') && (report.summary.totalAppointments ?? 0) > 0 && (
                  <Chip icon={<EventAvailableIcon />} label={`Зустрічей: ${report.summary.totalAppointments}`} variant="outlined" color="success" />
                )}
                {sections.includes('events') && report.summary.totalEvents > 0 && (
                  <Chip icon={<EventNoteIcon />} label={`Подій: ${report.summary.totalEvents}`} variant="outlined" color="primary" />
                )}
                {sections.includes('measurements') && report.summary.totalMeasurements > 0 && (
                  <Chip icon={<MonitorWeightIcon />} label={`Вимірювань: ${report.summary.totalMeasurements}`} variant="outlined" color="secondary" />
                )}
                {sections.includes('files') && report.summary.totalFiles > 0 && (
                  <Chip icon={<InsertDriveFileIcon />} label={`Файлів: ${report.summary.totalFiles}`} variant="outlined" />
                )}
              </Box>

              {/* Empty state */}
              {!report.profile &&
                !report.metrics &&
                !report.goals &&
                (report.summary.totalAppointments ?? 0) === 0 &&
                report.summary.totalEvents === 0 &&
                report.summary.totalMeasurements === 0 &&
                report.summary.totalFiles === 0 &&
                (report.questionnaires?.length ?? 0) === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '30vh',
                      gap: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <AssessmentIcon sx={{ fontSize: 56, opacity: 0.25 }} />
                    <Typography variant="body1">За обраний період немає даних</Typography>
                  </Box>
                )}

              {/* Profile */}
              {sections.includes('profile') && report.profile && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="subtitle1" fontWeight={600}>Профіль</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
                    {report.profile.general.bornYear != null && (
                      <ProfileField label="Рік народження" value={String(report.profile.general.bornYear)} />
                    )}
                    {report.profile.general.gender != null && (
                      <ProfileField label="Стать" value={report.profile.general.gender === 1 ? 'Чоловіча' : 'Жіноча'} />
                    )}
                    {report.profile.general.height != null && (
                      <ProfileField label="Зріст" value={`${report.profile.general.height} см`} />
                    )}
                    {report.profile.measurements.weight != null && (
                      <ProfileField label="Вага" value={`${parseFloat(String(report.profile.measurements.weight))} кг`} />
                    )}
                    {report.profile.general.activityLevel && (
                      <ProfileField
                        label="Рівень активності"
                        value={ACTIVITY_LEVEL_LABELS[report.profile.general.activityLevel] ?? report.profile.general.activityLevel}
                      />
                    )}
                    {report.profile.general.goal && (
                      <ProfileField
                        label="Мета"
                        value={GOAL_LABELS[report.profile.general.goal] ?? report.profile.general.goal}
                      />
                    )}
                    {report.profile.medical.complaints && (
                      <ProfileField label="Скарги" value={report.profile.medical.complaints} />
                    )}
                    {report.profile.medical.chronic_diseases && (
                      <ProfileField label="Хронічні хвороби" value={report.profile.medical.chronic_diseases} />
                    )}
                    {report.profile.nutrition.meals_count != null && (
                      <ProfileField label="Прийомів їжі" value={`${report.profile.nutrition.meals_count} на день`} />
                    )}
                    {report.profile.nutrition.daily_water_liters != null && (
                      <ProfileField label="Вода (звичка)" value={`${report.profile.nutrition.daily_water_liters} л/день`} />
                    )}
                    {report.profile.nutrition.dietary_restrictions && (
                      <ProfileField label="Дієтичні обмеження" value={report.profile.nutrition.dietary_restrictions} />
                    )}
                  </Box>
                </Paper>
              )}

              {/* Metrics */}
              {sections.includes('metrics') && report.metrics && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon fontSize="small" color="info" />
                    <Typography variant="subtitle1" fontWeight={600}>Метрики</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
                    {report.metrics.bmi != null && <ProfileField label="ІМТ" value={parseFloat(String(report.metrics.bmi)).toFixed(2)} />}
                    {report.metrics.bmr != null && <ProfileField label="БМР (базовий метаболізм)" value={`${report.metrics.bmr} ккал/день`} />}
                    {report.metrics.amr != null && <ProfileField label="АМР (активний метаболізм)" value={`${report.metrics.amr} ккал/день`} />}
                    {report.metrics.wdn != null && <ProfileField label="Норма води (базова)" value={`${report.metrics.wdn} мл`} />}
                    {report.metrics.wdna != null && <ProfileField label="Норма води (з активністю)" value={`${report.metrics.wdna} мл`} />}
                    {report.metrics.whr != null && <ProfileField label="Індекс талія/стегна (WHR)" value={parseFloat(String(report.metrics.whr)).toFixed(2)} />}
                  </Box>
                </Paper>
              )}

              {/* Goals */}
              {sections.includes('goals') && report.goals && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlagIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle1" fontWeight={600}>Цілі на день</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
                    {report.goals.waterMl != null && <ProfileField label="Вода" value={`${report.goals.waterMl} мл`} />}
                    {report.goals.caloriesIntake != null && <ProfileField label="Калорії" value={`${report.goals.caloriesIntake} ккал`} />}
                    {report.goals.caloriesBurned != null && <ProfileField label="Витрати на активність" value={`${report.goals.caloriesBurned} ккал`} />}
                    {report.goals.proteinG != null && <ProfileField label="Білки" value={`${report.goals.proteinG} г`} />}
                    {report.goals.fatG != null && <ProfileField label="Жири" value={`${report.goals.fatG} г`} />}
                    {report.goals.carbsG != null && <ProfileField label="Вуглеводи" value={`${report.goals.carbsG} г`} />}
                  </Box>
                </Paper>
              )}

              {/* Appointments */}
              {sections.includes('appointments') && (report.appointments?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventAvailableIcon fontSize="small" color="success" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Зустрічі ({report.summary.totalAppointments ?? report.appointments!.length})
                    </Typography>
                  </Box>
                  <Divider />
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата / Час</TableCell>
                        <TableCell>Експерт</TableCell>
                        <TableCell>Тривалість</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Підсумок</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.appointments ?? []).map((appt) => (
                        <TableRow key={appt.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">
                              {format(new Date(appt.scheduledAt), 'dd.MM.yyyy', { locale: uk })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(appt.scheduledAt), 'HH:mm', { locale: uk })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{appt.expertName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{appt.durationMinutes} хв</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={APPOINTMENT_STATUS_LABELS[appt.status]}
                              size="small"
                              color={APPOINTMENT_STATUS_COLORS[appt.status]}
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            {appt.outcome ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {appt.outcome}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {/* Events — grouped by eventType → date → events */}
              {sections.includes('events') && (report.events?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventNoteIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Події ({report.summary.totalEvents})
                    </Typography>
                  </Box>

                  {(report.events ?? []).map((group, gi) => (
                    <React.Fragment key={group.eventType.id}>
                      <Divider />
                      {/* Заголовок типу події */}
                      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover' }}>
                        <EventTypeIcon code={group.eventType.code} size={16} />
                        <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {group.eventType.name}
                        </Typography>
                      </Box>

                      {group.dates.map((dateGroup, di) => (
                        <React.Fragment key={dateGroup.date}>
                          {/* Підзаголовок дати */}
                          <Box sx={{ px: 2, py: 0.5, bgcolor: gi % 2 === 0 ? 'background.default' : 'transparent' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              {format(new Date(dateGroup.date), 'EEEE, d MMMM', { locale: uk })}
                            </Typography>
                          </Box>
                          <Table size="small">
                            {di === 0 && gi === 0 && (
                              <TableHead>
                                <TableRow>
                                  <TableCell>Час</TableCell>
                                  <TableCell>Назва</TableCell>
                                  <TableCell>Джерело</TableCell>
                                  <TableCell align="center">Виконано</TableCell>
                                </TableRow>
                              </TableHead>
                            )}
                            <TableBody>
                              {dateGroup.events.map((event) => {
                                const totals = event.details?.totals;
                                const rawInput = event.details?.rawInput;
                                return (
                                <TableRow key={event.id} hover>
                                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', pt: 1.2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {format(new Date(event.scheduledLdt), 'HH:mm', { locale: uk })}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{event.title}</Typography>
                                    {rawInput && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                                        {rawInput}
                                      </Typography>
                                    )}
                                    {totals && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        {[
                                          totals.kcal != null && `${totals.kcal} ккал`,
                                          totals.protein != null && `Б ${totals.protein}г`,
                                          totals.fat != null && `Ж ${totals.fat}г`,
                                          totals.carbs != null && `В ${totals.carbs}г`,
                                        ].filter(Boolean).join(' · ')}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', pt: 1.2 }}>
                                    {event.isOwnEvent ? (
                                      <Chip
                                        icon={<PersonIcon />}
                                        label="Власна"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    ) : (
                                      <Chip
                                        label={event.expert?.name ?? 'Експерт'}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell align="center" sx={{ verticalAlign: 'top', pt: 1.2 }}>
                                    {event.isCompleted ? (
                                      <CheckCircleIcon fontSize="small" color="success" />
                                    ) : (
                                      <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                                    )}
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </Paper>
              )}

              {/* Latest measurements summary */}
              {sections.includes('measurements') && (report.latestMeasurements?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MonitorWeightIcon fontSize="small" color="secondary" />
                    <Typography variant="subtitle1" fontWeight={600}>Актуальні показники</Typography>
                  </Box>
                  <Divider />
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Показник</TableCell>
                        <TableCell align="right">Значення</TableCell>
                        <TableCell align="right">Дата</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.latestMeasurements ?? []).map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell>
                            <Typography variant="body2">{m.measurementType.name}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={500}>{m.formattedValue}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(m.measuredAt), 'dd.MM.yyyy', { locale: uk })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {/* Measurements — grouped by type */}
              {sections.includes('measurements') && (report.measurements?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MonitorWeightIcon fontSize="small" color="secondary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Виміри тіла ({report.summary.totalMeasurements})
                    </Typography>
                  </Box>
                  {(report.measurements ?? []).map((group, gi) => (
                    <React.Fragment key={group.type.id}>
                      {gi > 0 && <Divider />}
                      <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {group.type.name}
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Дата виміру</TableCell>
                            <TableCell align="right">Значення</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.entries.map((entry) => (
                            <TableRow key={entry.id} hover>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(entry.measuredAt), 'dd.MM.yyyy HH:mm', { locale: uk })}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500}>
                                  {entry.formattedValue}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </React.Fragment>
                  ))}
                </Paper>
              )}

              {/* Files */}
              {sections.includes('files') && (report.files?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsertDriveFileIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Файли ({report.files?.length ?? 0})
                    </Typography>
                  </Box>
                  <Divider />
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Файл</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Розмір</TableCell>
                        <TableCell>Дата</TableCell>
                        <TableCell>Коментар</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.files ?? []).map((file) => (
                        <TableRow key={file.id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              component="a"
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {file.originalFilename}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={file.fileType} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatBytes(file.size)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(file.createdAt), 'dd.MM.yyyy', { locale: uk })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {file.comment || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {/* Опитувальники */}
              {sections.includes('questionnaires') && (report.questionnaires?.length ?? 0) > 0 && (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QuestionAnswerIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Опитувальники ({report.questionnaires!.length})
                    </Typography>
                  </Box>
                  {report.questionnaires!.map((q, gi) => (
                    <React.Fragment key={q.surveyId}>
                      {gi > 0 && <Divider />}
                      <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {q.templateName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Завершено {format(new Date(q.completedAt), 'dd.MM.yyyy', { locale: uk })}
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableBody>
                          {q.answers.map((a, ai) => (
                            <TableRow key={ai} hover>
                              <TableCell sx={{ width: '40%', verticalAlign: 'top' }}>
                                <Typography variant="body2">{a.question}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">{a.answer}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </React.Fragment>
                  ))}
                </Paper>
              )}
            </Box>
          )}
        </PageCard>
        )}

        {activeTab === 'history' && (
          <PageCard>
            <PageHint iDoc={TabIndices.REPORTS_AI_HISTORY} />

            {aiHistoryLoading && (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {aiHistoryError && <Alert severity="error">{aiHistoryError}</Alert>}

            {!aiHistoryLoading && !aiHistoryError && aiHistory.length === 0 && (
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ py: 6, gap: 2, color: 'text.secondary' }}>
                <HistoryIcon sx={{ fontSize: 48, opacity: 0.25 }} />
                <Typography variant="body1">Ще немає збережених AI-аналізів</Typography>
                <Typography variant="body2" color="text.disabled">
                  Перейдіть на вкладку «Звіт» та натисніть «AI-аналіз»
                </Typography>
              </Box>
            )}

            {!aiHistoryLoading && aiHistory.length > 0 && (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Тип</TableCell>
                      <TableCell>Період</TableCell>
                      <TableCell>Секції</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aiHistory.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {AI_REPORT_TYPE_LABELS[r.reportType] ?? r.reportType}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {r.periodFrom} – {r.periodTo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {r.sections ? `${r.sections.length} секцій` : 'Всі секції'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={AI_REPORT_STATUS_LABELS[r.status] ?? r.status}
                            size="small"
                            color={AI_REPORT_STATUS_COLORS[r.status] ?? 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(r.createdAt).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={r.status !== 'completed' || loadingReport}
                            onClick={() => handleViewReport(r.id)}
                          >
                            Переглянути
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <UniversalPagination
                  page={aiHistoryPage}
                  count={Math.ceil(aiHistoryTotal / aiHistoryLimit)}
                  limit={aiHistoryLimit}
                  total={aiHistoryTotal}
                  limitOptions={[10, 25, 50]}
                  storageKey={AI_HISTORY_LIMIT_KEY}
                  onPageChange={(p) => setAiHistoryPage(p)}
                  onLimitChange={(l) => { setAiHistoryLimit(l); setAiHistoryPage(1); }}
                />
              </>
            )}
          </PageCard>
        )}
      </Box>

      {/* AI-аналіз модалка */}
      <BaseDialog
        open={aiAnalysisOpen}
        onClose={handleCloseAiAnalysis}
        maxWidth="md"
        title={<Box display="flex" alignItems="center" gap={1}><AutoAwesomeIcon fontSize="small" />AI-аналіз</Box>}
        actions={
          <>
            <Button
              startIcon={<PsychologyIcon />}
              onClick={handleOpenAiContext}
              disabled={loadingAiContext || loadingAiAnalysis}
            >
              AI-контекст
            </Button>
            {aiAnalysisText && (
              <>
                <Button startIcon={<DownloadIcon />} onClick={handleDownloadAiAnalysis}>
                  Завантажити
                </Button>
                <Button
                  sx={{ mr: 'auto' }}
                  startIcon={aiAnalysisCopied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  onClick={handleCopyAiAnalysis}
                  color={aiAnalysisCopied ? 'success' : 'inherit'}
                >
                  {aiAnalysisCopied ? 'Скопійовано' : 'Копіювати'}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              startIcon={loadingAiAnalysis ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleRunAiAnalysis}
              disabled={loadingAiAnalysis}
            >
              {aiAnalysisText ? 'Повторити' : 'Аналізувати'}
            </Button>
          </>
        }
      >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Поле промпту — завжди видиме */}
            <TextField
              multiline
              minRows={2}
              maxRows={5}
              placeholder="Необов'язково: введіть свою інструкцію для AI. Наприклад: «Ти нутриціолог. Зроби акцент на харчуванні.» Якщо залишити порожнім — використовується стандартний аналіз."
              value={aiAnalysisPrompt}
              onChange={(e) => setAiAnalysisPrompt(e.target.value)}
              disabled={loadingAiAnalysis}
              fullWidth
            />

            {/* Результат або лоадер */}
            {loadingAiAnalysis && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={3}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Аналізую дані...</Typography>
              </Box>
            )}
            {!loadingAiAnalysis && aiAnalysisText && (
              <>
                {aiAnalysisFromCache && (
                  <Box>
                    <Chip
                      icon={<CachedIcon fontSize="small" />}
                      label="Результат з кешу"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
                <Box
                  dangerouslySetInnerHTML={{ __html: aiAnalysisText }}
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& h2, & h3': { mt: 2, mb: 0.5, fontWeight: 600 },
                    '& p': { mt: 0, mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.25 },
                  }}
                />
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: 'primary.light',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Хочете більш детальну консультацію?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Цей аналіз сформований AI. Персональну консультацію з урахуванням вашої ситуації може надати живий експерт.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    href="/experts"
                    onClick={handleCloseAiAnalysis}
                  >
                    Знайти експерта
                  </Button>
                </Paper>
              </>
            )}
          </Box>
      </BaseDialog>

      {/* AI-контекст модалка */}
      <BaseDialog
        open={aiContextOpen}
        onClose={() => setAiContextOpen(false)}
        maxWidth="md"
        title={<Box display="flex" alignItems="center" gap={1}><PsychologyIcon fontSize="small" />AI-контекст</Box>}
        actions={
          <Button
            sx={{ mr: 'auto' }}
            startIcon={aiContextCopied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            onClick={() => {
              navigator.clipboard.writeText(aiContextMarkdown).then(() => {
                setAiContextCopied(true);
                setTimeout(() => setAiContextCopied(false), 2000);
              });
            }}
            disabled={!aiContextMarkdown}
            color={aiContextCopied ? 'success' : 'inherit'}
          >
            {aiContextCopied ? 'Скопійовано' : 'Копіювати'}
          </Button>
        }
      >
        {loadingAiContext ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'text.primary',
            }}
          >
            {aiContextMarkdown}
          </Box>
        )}
      </BaseDialog>

      {/* Перегляд збереженого AI-аналізу */}
      <BaseDialog
        open={!!selectedReport}
        onClose={handleCloseReport}
        maxWidth="md"
        title={
          selectedReport
            ? <Box display="flex" alignItems="center" gap={1}>
                <AutoAwesomeIcon fontSize="small" />
                {`AI-аналіз: ${selectedReport.periodFrom} – ${selectedReport.periodTo}`}
              </Box>
            : ''
        }
        actions={
          selectedReport?.response ? (
            <>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport(selectedReport)}
              >
                Завантажити
              </Button>
              <Button
                sx={{ mr: 'auto' }}
                startIcon={selectedReportCopied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                onClick={handleCopySelectedReport}
                color={selectedReportCopied ? 'success' : 'inherit'}
              >
                {selectedReportCopied ? 'Скопійовано' : 'Копіювати'}
              </Button>
            </>
          ) : undefined
        }
      >
        {loadingReport && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {!loadingReport && selectedReport?.response && (
          <Box
            dangerouslySetInnerHTML={{ __html: selectedReport.response }}
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.7,
              color: 'text.primary',
              '& h2, & h3': { mt: 2, mb: 0.5, fontWeight: 600 },
              '& p': { mt: 0, mb: 1 },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& li': { mb: 0.25 },
            }}
          />
        )}
        {!loadingReport && !selectedReport?.response && (
          <Typography variant="body2" color="text.secondary">Текст аналізу недоступний</Typography>
        )}
      </BaseDialog>
    </DashboardLayout>
  );
};
